import { useDebounceFn, useLatest, useUpdateEffect } from 'ahooks';
import { groupBy, merge, pick } from 'lodash';
import React, { useEffect, useImperativeHandle, useRef, useState } from 'react';
import { LiteralUnion } from 'type-fest';
import { Button, Checkbox, InputNumber, Popover } from '@meitu/saas-ui';
import { Editor } from '@meitu/saas-editor-core';
import { ActiveTableInfo, CellsDisplayInfo, TableLayer } from '@meitu/saas-editor-core/es/layers/table-layer';
import type { CellsActions } from '@meitu/saas-editor-core/es/layers/table-layer/actions/cells-actions';
import {
    DEFAULT_TABLE_CELL__TEXT_FONT_SIZE,
    DEFAULT_TABLE_CELL__VERTICAL_ALIGN,
    MAX_COL_COUNT,
    MAX_ROW_COUNT,
    MIN_BORDER_WIDTH,
} from '@meitu/saas-editor-core/es/layers/table-layer/default-configs';
import tablePresets from '@meitu/saas-editor-core/es/layers/table-layer/presets';
import { isSafari } from '@meitu/saas-editor-core/es/utils/browser-kit';
import { DEFAULT_BACKGROUND_COLOR } from '@meitu/saas-editor-core/es/version-parse';
import {
    Border,
    BorderStyle,
    ITableLayer,
    LayerType,
    TextAlignType,
    TextVerticalAlign,
    TextWritingType,
} from '@meitu/saas-editor-core/es/version-types';
import IconFont from '@meitu/xdesign-icon-font';
import { ColorGroup, TextFontFamily } from '@meitu/xdesign-widgets';
import { type FontItem, type ITextFontFamilyProps } from '@meitu/xdesign-widgets/dist/textFontFamily';
import { FontSizeSelector } from '@/components/font-size-selector';
import { FontStyleGroup } from '@/components/font-style-group';
import LabelAction from '@/components/label-action';
import { TextAlignmentGroup } from '@/components/text-alignment-group';
import { useLocaleStore } from '@/i18n';
import borderModeBottomImage from './assets/border-mode-bottom.png';
import borderModeFullImage from './assets/border-mode-full.png';
import borderModeLeftImage from './assets/border-mode-left.png';
import borderModeNoneImage from './assets/border-mode-none.png';
import borderModeRightImage from './assets/border-mode-right.png';
import borderModeTopImage from './assets/border-mode-top.png';
import borderModeWrapImage from './assets/border-mode-wrap.png';
import line0Svg from './assets/line-0.svg';
import line1Svg from './assets/line-1.svg';
import line2Svg from './assets/line-2.svg';
import line3Svg from './assets/line-3.svg';
import line4Svg from './assets/line-4.svg';
import lineStyleHandlerSvg from './assets/line-style-handler.svg';
import './table-layer-panel.less';

type BorderState = Border & {
    mode: 'wrap' | 'full' | 'top' | 'left' | 'right' | 'bottom' | 'none';
    isBrandColor: boolean;
};

type TableTextAttrActionType = 'bold' | 'italic' | 'underline' | 'lineThrough';
type TableTextAlignActionType = 'left' | 'top' | 'vertical-center' | 'bottom' | 'center' | 'right' | 'justify';

export type ActionInfo =
    | {
          target: 'textAutoWrap';
          data: boolean;
      }
    | {
          target: 'textAttr';
          type: TableTextAttrActionType;
          data: boolean;
      }
    | {
          target: 'textAlign';
          type: TableTextAlignActionType;
      }
    | {
          target: 'textFontFamily';
          data: FontItem;
      }
    | {
          target: 'textFontFamilyDropdownVisible';
      }
    | {
          target: 'fontSize';
          data: number;
      }
    | {
          target: 'fontSizeClick';
      }
    | {
          target: 'textColor';
          data: { value: string; isBrandColor: boolean };
      }
    | {
          target: 'rowOrColCount';
          type: 'row' | 'col';
          data: number;
      }
    | {
          target: 'cellBackground';
          data: { value: string; isBrandColor: boolean };
      }
    | {
          target: 'border';
          type: 'mode' | 'color' | 'style' | 'width';
          data:
              | LiteralUnion<BorderState['mode'] | BorderStyle, string>
              | { value: string; isBrandColor: boolean }
              | number;
      }
    | {
          target: 'tablePreset' | 'mergeCells' | 'cancelMergeCell';
          data?: string;
      }
    | {
          target: 'onShowBrandColorsTab';
      };

interface ActiveTablePanelProps {
    editor: Editor;
    isVipUser?: boolean;
    fontList: FontItem[];
    handleShowVip?: () => void;
    children: (param: { TablePanel: React.ReactNode; TextPanel: React.ReactNode }) => React.ReactElement;
    onAction?: (actionInfo: ActionInfo) => void;
    textFontFamilyComponentUserBizSearchProps?: Pick<ITextFontFamilyProps, 'searchFontData' | 'searchDebounceDelay'>;
}

export interface ActiveTablePanelRef {
    /** 业务使用品牌库文字样式, 针对当前的单元格or选中区or整个表格做修改操作 */
    changeByTextStyle: (textStyle: {
        fontId: number;
        fontFamily: string;
        fontName: string;
        fontSize: number;
        bold: boolean;
        italic: boolean;
        name: string;
    }) => void;
}

const useLayerAndActiveTableInfo = (editor: Editor) => {
    const [layer, setLayer] = useState<ITableLayer>(() => editor.active.getPick(true) as ITableLayer);
    const [activeTableInfo, setActiveTableInfo] = useState<ActiveTableInfo>(() =>
        editor.getInstance(TableLayer).getTableInfo(layer),
    );
    console.log('===layer', layer);
    const layerRef = useLatest(layer);
    const isBaseOperateRef = useRef(false);
    const isLineDraggingRef = useRef(false);
    const isTextEditingRef = useRef(false);

    useEffect(() => {
        const rmBaseOperateChangeListenerId = editor.onBaseOperateChange((isBaseOperate) => {
            isBaseOperateRef.current = isBaseOperate;
        });
        const activeChangeListenerId = editor.active.onChange(() => {
            // 基础操作、表格行列线拖拽、文字编辑时, 都不应该变化数据, 不然getTableInfo计算量巨大, 大的表格会直接卡死
            // 除了缩放操作的其他基础操作时不会触发editor.active.onChange, 所以不用判断
            if (isBaseOperateRef.current) return;
            if (isLineDraggingRef.current) return;
            const newLayer = editor.active.getPick(true) as ITableLayer | null;
            if (!newLayer || newLayer.layerType !== LayerType.TABLE) return;
            if (isTextEditingRef.current) {
                // fix: 文字编辑时, 可能设置了其他属性值, 这里只更新选中相关的信息, 修复数据展示问题
                const selectedCellStyleInfo = editor.getInstance(TableLayer).getSelectedCellStyleInfo(newLayer.data);
                setActiveTableInfo((old) => ({
                    ...old,
                    selectedCellStyleInfo,
                }));
                return;
            }

            setLayer(newLayer);
            const newActiveTableInfo = editor.getInstance(TableLayer).getTableInfoOnUpdate(newLayer);
            if (newActiveTableInfo) {
                setActiveTableInfo(newActiveTableInfo);
            }
        });
        return () => {
            rmBaseOperateChangeListenerId();
            editor.off(activeChangeListenerId);
        };
    }, []);

    useEffect(() => {
        const rmLineDraggingListener = editor.getInstance(TableLayer).onIsLineDraggingStateUpdate((isDragging) => {
            isLineDraggingRef.current = isDragging;
        });
        const rmTextEditingListener = editor.getInstance(TableLayer).onIsTextEditingStateUpdate((isEditing) => {
            isTextEditingRef.current = isEditing;
        });
        return () => {
            rmLineDraggingListener();
            rmTextEditingListener();
        };
    }, []);

    // 选中区域变化时, 也需要更新activeTableInfo, 因为选中区域是表格编辑态的内部状态, 不会触发表格数据的自动更新
    useEffect(() => {
        const rmListener = editor.getInstance(TableLayer).onSelectedInfoUpdate(() => {
            const newLayer = editor.stage.getLayer(layerRef.current?.id || '') as ITableLayer | undefined;
            if (!newLayer) return;
            const newActiveTableInfo = editor.getInstance(TableLayer).getTableInfoOnUpdate(newLayer);
            if (newActiveTableInfo) {
                setActiveTableInfo(newActiveTableInfo);
            }
        });
        return rmListener;
    }, []);

    return {
        layer,
        activeTableInfo,
    };
};

const useUsingFontList = (editor: Editor, fontList: FontItem[]) => {
    const getUsingFontList = () => {
        const usingFontIds = editor.font
            .getUsingList(editor.active.getLayers())
            .map((f) => f.id)
            .filter(Boolean) as number[];
        return usingFontIds.map((id) => fontList.find((f) => f.id === id)).filter(Boolean) as FontItem[];
    };

    const [usingFontList, setUsingFontList] = useState<FontItem[]>(getUsingFontList);
    const refreshUsingFontList = () => setUsingFontList(getUsingFontList());
    return { usingFontList, refreshUsingFontList };
};

const useBorderState = (() => {
    // 记录最后一次操作的borderState, 下次打开操作面板时, 继续维持这个borderState
    const currentBorderStateRef: { current: BorderState } = {
        current: {
            mode: 'full',
            color: '#000000ff',
            style: 'line-0',
            width: 4,
            isBrandColor: false,
        },
    };
    const useBorderState = () => {
        const [borderState, setBorderState] = useState(currentBorderStateRef.current);
        useEffect(() => {
            currentBorderStateRef.current = borderState;
        }, [borderState]);
        return [borderState, setBorderState] as const;
    };
    return useBorderState;
})();

/**
 * 使用ActiveTablePanel时, 就默认表格已经处于编辑态了, 否则会有问题
 */
const ActiveTablePanel = React.forwardRef<ActiveTablePanelRef, ActiveTablePanelProps>(
    (
        {
            editor,
            fontList,
            isVipUser,
            handleShowVip,
            children,
            onAction,
            textFontFamilyComponentUserBizSearchProps: fontFamilyComponentUserBizSearchProps,
        },
        ref,
    ) => {
        const { layer, activeTableInfo } = useLayerAndActiveTableInfo(editor);
        const { $t } = useLocaleStore();
        const layerRef = useLatest(layer);
        const { usingFontList, refreshUsingFontList } = useUsingFontList(editor, fontList);
        const activeTableInfoRef = useLatest(activeTableInfo);

        const { activeTableLayerActions } = editor.getInstance(TableLayer);

        const getAction = <K extends keyof CellsActions>(actionType: K): CellsActions[K] => {
            const activeTableInfo = activeTableInfoRef.current;
            const actions = activeTableInfo?.hasSelectedInfo
                ? activeTableLayerActions.selectedCellsActions
                : activeTableLayerActions.allCellActions;
            return actions[actionType];
        };

        useImperativeHandle(
            ref,
            () => ({
                changeByTextStyle: (textStyle) => {
                    const tableLayer = layerRef.current;
                    if (!tableLayer) return;
                    editor.font.register(tableLayer, textStyle.fontFamily, true);
                    getAction('setTextStyle')(textStyle);
                },
            }),
            [],
        );

        const [borderState, setBorderState] = useBorderState();

        const { run: debouncedSetBorderState } = useDebounceFn(setBorderState, { wait: 200 });

        useUpdateEffect(
            () => onAction?.({ target: 'border', type: 'mode', data: borderState.mode }),
            [borderState.mode],
        );
        useUpdateEffect(
            () =>
                onAction?.({
                    target: 'border',
                    type: 'color',
                    data: { value: borderState.color, isBrandColor: borderState.isBrandColor },
                }),
            [borderState.color],
        );
        useUpdateEffect(
            () => onAction?.({ target: 'border', type: 'style', data: borderState.style }),
            [borderState.style],
        );
        useUpdateEffect(
            () => onAction?.({ target: 'border', type: 'width', data: borderState.width }),
            [borderState.width],
        );
        useUpdateEffect(() => {
            if (borderState.mode === 'none') {
                getAction('clearBorders')();
            } else {
                getAction('appendBorders')(borderState.mode, pick(borderState, ['style', 'width', 'color']));
            }
        }, [borderState]);

        const setBackgroundColor = (backgroundColor: { value: `#${string}`; isBrandColor: boolean }) => {
            onAction?.({ target: 'cellBackground', data: backgroundColor });
            getAction('setBackgroundColor')(backgroundColor.value);
        };
        const mergeCells = () => {
            onAction?.({ target: 'mergeCells' });
            getAction('mergeCells')();
        };
        const cancelMergeCells = () => {
            onAction?.({ target: 'cancelMergeCell' });
            getAction('cancelMergeCells')();
        };
        const handlePreset = (type: string) => {
            onAction?.({ target: 'tablePreset', data: type });
        };
        const setFontFamily = (data: FontItem) => {
            onAction?.({ target: 'textFontFamily', data });
            getAction('setFontFamily')(data.font_name);
            refreshUsingFontList();
        };
        const { run: debouncedSetFontSize } = useDebounceFn(
            (value: number) => {
                onAction?.({ target: 'fontSize', data: value });
                getAction('setFontSize')(value);
            },
            { wait: 200 },
        );
        const { run: debouncedSetTextColor } = useDebounceFn(
            (color: { value: `#${string}`; isBrandColor: boolean }) => {
                onAction?.({ target: 'textColor', data: color });
                getAction('setTextColor')(color.value);
            },
            { wait: 200 },
        );
        const { run: debouncedSetBackgroundColor } = useDebounceFn(setBackgroundColor, {
            wait: 200,
        });

        const cellsDisplayInfo = (() => {
            const cellsDisplayInfo = merge(
                {},
                activeTableInfo.allCellsStyleInfo,
                activeTableInfo.selectedCellStyleInfo,
            ) as Partial<CellsDisplayInfo>;
            cellsDisplayInfo.textList = activeTableInfo.selectedCellStyleInfo
                ? activeTableInfo.selectedCellStyleInfo.textList
                : activeTableInfo.allCellsStyleInfo?.textList;
            return cellsDisplayInfo;
        })();

        const getRowOrColInputComponent = (mode: 'row' | 'col') => (
            <InputNumber
                value={mode === 'row' ? activeTableInfo.rowCount : activeTableInfo.colCount}
                max={mode === 'row' ? MAX_ROW_COUNT : MAX_COL_COUNT}
                min={1}
                onChange={(value, isMouseWheel) => {
                    if (value === null || value <= 0) return;
                    activeTableLayerActions.setRowOrColCount({
                        mode,
                        count: value,
                        isMouseWheel,
                    });
                }}
                onChangeAfter={(value) => {
                    if (value === null || value <= 0) return;
                    onAction?.({
                        target: 'rowOrColCount',
                        type: mode,
                        data: value,
                    });
                }}
                showStepBtn={false}
                precision={0}
                enableMouseWheel
                onMouseWheelEnd={activeTableLayerActions.afterMouseWheelSetRowOrColCount}
            />
        );

        const getTablePanel = () => (
            <div className="table-panel">
                <PresetOverwriteSelector
                    currentPresetId={activeTableInfo.presetId}
                    overwritePreset={activeTableLayerActions.overwritePreset}
                    onPreset={handlePreset}
                />
                <LabelAction title={$t((v) => v.tableLayer.panel.count)} align>
                    <div className="table-panel__row-and-col">
                        <div className="table-panel__row-input">
                            {getRowOrColInputComponent('row')}
                            <span>{$t((v) => v.tableLayer.rowShort)}</span>
                        </div>
                        <div className="table-panel__col-input">
                            {getRowOrColInputComponent('col')}
                            <span>{$t((v) => v.tableLayer.columnShort)}</span>
                        </div>
                    </div>
                </LabelAction>
                <LabelAction title={$t((v) => v.tableLayer.panel.color)} align>
                    <ColorGroup
                        popupTriggerSize={{
                            width: 88,
                            height: 26,
                        }}
                        showMultColor={cellsDisplayInfo.backgroundColor?.isMulti}
                        color={
                            cellsDisplayInfo.backgroundColor?.isMulti
                                ? DEFAULT_BACKGROUND_COLOR
                                : (cellsDisplayInfo.backgroundColor?.firstValue ?? '')
                        }
                        isGradient={false}
                        setColor={(color, _, { isBrandColor }) =>
                            debouncedSetBackgroundColor({
                                value: color as `#${string}`,
                                isBrandColor,
                            })
                        }
                        onToggleBizTab={(bizTab) => {
                            if (bizTab === 'team') {
                                onAction?.({ target: 'onShowBrandColorsTab' });
                            }
                        }}
                    />
                </LabelAction>
                <LabelAction title={$t((v) => v.tableLayer.panel.borderStyle)} align>
                    <div style={{ display: 'flex' }}>
                        <BorderModeSelect
                            tempModeValue={borderState.mode}
                            onChangeTempModeValue={(mode) => debouncedSetBorderState((s) => ({ ...s, mode }))}
                        />
                        <ColorGroup
                            popupTriggerStyle={{ marginLeft: 4 }}
                            popupTriggerSize={{
                                width: 26,
                                height: 26,
                            }}
                            showMultColor={Array.isArray(borderState.color)}
                            color={Array.isArray(borderState.color) ? '' : borderState.color}
                            isGradient={false}
                            setColor={(color, _, { isBrandColor }) =>
                                debouncedSetBorderState((s) => ({
                                    ...s,
                                    color: color as `#${string}`,
                                    isBrandColor,
                                }))
                            }
                            showIcon={false}
                            disabled={borderState.mode === 'none'}
                            onToggleBizTab={(bizTab) => {
                                if (bizTab === 'team') {
                                    onAction?.({ target: 'onShowBrandColorsTab' });
                                }
                            }}
                        />
                    </div>
                </LabelAction>
                <LabelAction title={$t((v) => v.tableLayer.panel.lineStyle)} align>
                    <BorderStyleWidget
                        bold={borderState.width}
                        onChangeBold={(v) => debouncedSetBorderState((s) => ({ ...s, width: v }))}
                        tempStyleValue={borderState.style}
                        onChangeTempStyleValue={(style) => debouncedSetBorderState((s) => ({ ...s, style }))}
                        disabled={borderState.mode === 'none'}
                    />
                </LabelAction>
                <MergeBtn
                    activeTableInfo={activeTableInfo}
                    mergeCells={mergeCells}
                    cancelMergeCells={cancelMergeCells}
                />
                {process.env.NODE_ENV === 'development' && (
                    <>
                        <pre>
                            rowOrColSelectedRectInfo:&nbsp;
                            {JSON.stringify(activeTableInfo.debugInfo.rowOrColSelectedRectInfo, null, 2)}
                        </pre>
                        <pre>
                            cellsSelectedInfo:&nbsp;
                            {activeTableInfo.debugInfo.cellsSelectedInfo
                                ? JSON.stringify(
                                      {
                                          ...activeTableInfo.debugInfo.cellsSelectedInfo,
                                          cellInfoList: activeTableInfo.debugInfo.cellsSelectedInfo.cellInfoList.map(
                                              (c) => `${c.hIndex}-${c.vIndex}`,
                                          ),
                                      },
                                      null,
                                      2,
                                  )
                                : 'null'}
                        </pre>
                    </>
                )}
            </div>
        );

        const getTextPanel = () => (
            <div className="table-text-panel">
                <LabelAction title={$t((v) => v.textFontFamily.fontFamily)} align>
                    <TextFontFamily
                        value={cellsDisplayInfo.fontFamily}
                        usingFontList={usingFontList}
                        isVip={isVipUser}
                        allFontData={groupBy(fontList, 'category_name')}
                        onChange={setFontFamily}
                        onClickVipBtn={handleShowVip}
                        text={cellsDisplayInfo.textList?.join('')}
                        onDropdownVisibleChange={(visible) =>
                            visible && onAction?.({ target: 'textFontFamilyDropdownVisible' })
                        }
                        {...fontFamilyComponentUserBizSearchProps}
                    />
                </LabelAction>

                <LabelAction title={$t((v) => v.textFontSizeAndTextColor.size)} align>
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            width: '188px',
                        }}
                    >
                        <div onClick={() => onAction?.({ target: 'fontSizeClick' })}>
                            <FontSizeSelector
                                value={cellsDisplayInfo.fontSize ?? DEFAULT_TABLE_CELL__TEXT_FONT_SIZE}
                                onChange={(value) => value !== null && debouncedSetFontSize(value)}
                                // 加了性能也不好(没做处理), 所以先去掉
                                disableMouseWheel
                            />
                        </div>
                        <ColorGroup
                            showMultColor={cellsDisplayInfo.color?.isMulti}
                            popupTriggerSize={{
                                width: 92,
                                height: 34,
                            }}
                            color={cellsDisplayInfo.color?.isMulti ? '' : (cellsDisplayInfo.color?.firstValue ?? '')}
                            setColor={(color, _, { isBrandColor }) =>
                                debouncedSetTextColor({ value: color as `#${string}`, isBrandColor })
                            }
                            isGradient={false}
                            placement="left"
                            onToggleBizTab={(bizTab) => {
                                if (bizTab === 'team') {
                                    window._stat_track?.('hb_layers_func_click', {
                                        func_id: '1_107',
                                    });
                                }
                            }}
                        />
                    </div>
                </LabelAction>

                <FontStyleGroup
                    bold={cellsDisplayInfo.bold}
                    writingMode={TextWritingType.HORIZONTAL_TB}
                    italic={cellsDisplayInfo.italic}
                    lineThrough={cellsDisplayInfo.lineThrough}
                    underline={cellsDisplayInfo.underline}
                    closedFeatures={['writingMode']}
                    onChange={(kind, value) => {
                        onAction?.({
                            target: 'textAttr',
                            type: kind as TableTextAttrActionType,
                            data: value as boolean,
                        });
                        getAction('setTextAttr')(kind as TableTextAttrActionType, value as boolean);
                    }}
                />

                <TextAlignmentGroup
                    textAlign={cellsDisplayInfo.textAlign ?? TextAlignType.LEFT}
                    verticalAlign={
                        (cellsDisplayInfo.verticalAlign ?? DEFAULT_TABLE_CELL__VERTICAL_ALIGN) as TextVerticalAlign
                    }
                    writingMode={TextWritingType.HORIZONTAL_TB}
                    onChange={(kind, value) => {
                        let resultValue: TableTextAlignActionType;
                        if (kind === 'textAlign') {
                            resultValue = value;
                        } else {
                            resultValue = (
                                {
                                    top: 'top',
                                    center: 'vertical-center',
                                    bottom: 'bottom',
                                } as const
                            )[value];
                        }
                        onAction?.({ target: 'textAlign', type: resultValue });
                        getAction('setTextAlign')(resultValue);
                    }}
                />

                {/* TODO: safari和秀秀内嵌暂时禁用取消自动换行功能 (不自动换行, 生成图片文字永远是上对齐的) */}
                {!isSafari && (
                    <div className="table-panel__text-auto-wrap">
                        <Checkbox
                            checked={!cellsDisplayInfo.disableAutoWrap}
                            onChange={(e) => {
                                const { checked } = e.target;
                                onAction?.({ target: 'textAutoWrap', data: checked });
                                getAction('setDisableAutoWrap')(!checked);
                            }}
                        >
                            {$t((v) => v.tableLayer.panel.autoWrap)}
                        </Checkbox>
                    </div>
                )}
            </div>
        );

        return children({ TablePanel: getTablePanel(), TextPanel: getTextPanel() });
    },
);

ActiveTablePanel.displayName = 'ActiveTablePanel';

export default ActiveTablePanel;

const PresetOverwriteSelector: React.FC<{
    currentPresetId?: string;
    overwritePreset: (presetId: string) => void;
    onPreset: (type: string) => void;
}> = ({ currentPresetId, overwritePreset, onPreset }) => {
    const { $t } = useLocaleStore();
    const wrapperElRef = useRef<HTMLDivElement>(null);
    const [popup, setPopup] = useState(false);
    useEffect(() => {
        const onClick = (e: MouseEvent) => {
            if (e.target instanceof HTMLElement && !wrapperElRef.current?.contains(e.target)) {
                setPopup(false);
            }
        };
        window.addEventListener('click', onClick);
        return () => window.removeEventListener('click', onClick);
    }, []);
    const defaultPreset = tablePresets[0];
    const currentPresetPreview = tablePresets.find((p) => p.id === currentPresetId)?.preview ?? null;

    useEffect(() => {
        if (popup) {
            onPreset('isClick');
        }
    }, [popup]);
    return (
        <div ref={wrapperElRef} className="table__preset-overwrite-selector-wrapper">
            <div className="table__preset-overwrite-selector" onClick={() => setPopup((b) => !b)}>
                <div className="overwrite-current-preview">
                    <img src={currentPresetPreview ?? defaultPreset.preview} alt="" />
                </div>
                <div className="overwrite-selector-text">
                    {$t((v) => v.tableLayer.panel.selectStyle)}
                    <IconFont name="ChevronDownBlack" fontSize={12} style={{ marginLeft: 8 }} />
                </div>
            </div>

            {popup && (
                <div className="overwrite-popup">
                    {tablePresets.map((preset) => (
                        <div
                            key={preset.id}
                            className="overwrite-preset-item"
                            onClick={() => {
                                overwritePreset(preset.id);
                                onPreset('isUse');
                            }}
                        >
                            <img src={preset.preview} alt="" />
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

PresetOverwriteSelector.displayName = 'PresetOverwriteSelector';

const BorderModeSelect: React.FC<{
    /** 缓存的mode值 */
    tempModeValue: BorderState['mode'];
    onChangeTempModeValue: (v: BorderState['mode']) => void;
}> = ({ tempModeValue, onChangeTempModeValue }) => {
    const options: { image: string; value: BorderState['mode'] }[] = [
        { image: borderModeNoneImage, value: 'none' },
        { image: borderModeFullImage, value: 'full' },
        { image: borderModeWrapImage, value: 'wrap' },
        { image: borderModeLeftImage, value: 'left' },
        { image: borderModeTopImage, value: 'top' },
        { image: borderModeRightImage, value: 'right' },
        { image: borderModeBottomImage, value: 'bottom' },
    ];
    return (
        <Popover
            showArrow={false}
            destroyTooltipOnHide
            overlayClassName="table-panel__border-mode-popover"
            content={
                <ul className="table-panel__border-mode-list">
                    {options.map(({ value, image }) => (
                        <li
                            key={value}
                            data-active={tempModeValue === value}
                            style={{ backgroundImage: `url(${image})` }}
                            onClick={() => onChangeTempModeValue(value)}
                        ></li>
                    ))}
                </ul>
            }
            trigger="click"
            placement="bottomRight"
        >
            <div className="table-panel__border-mode-select">
                <div
                    style={{
                        width: 18,
                        height: 18,
                        backgroundImage: `url(${options.find((opt) => opt.value === tempModeValue)?.image})`,
                        backgroundSize: 'cover',
                    }}
                ></div>
                <IconFont fontSize={12} color="var(--content-input-lable, #ABADB2)" name="ChevronDownBlack" />
            </div>
        </Popover>
    );
};

BorderModeSelect.displayName = 'BorderModeSelect';

const BorderStyleWidget: React.FC<{
    bold: number;
    onChangeBold: (v: number) => void;
    /** 缓存的style值 */
    tempStyleValue: BorderStyle;
    onChangeTempStyleValue: (v: BorderStyle) => void;
    disabled?: boolean;
}> = ({ bold, onChangeBold, tempStyleValue, onChangeTempStyleValue, disabled = false }) => {
    return (
        <div
            className="table-panel__border-style"
            style={disabled ? { cursor: 'not-allowed' } : {}}
            onClickCapture={(e) => disabled && e.stopPropagation()}
        >
            <InputNumber
                disabled={disabled}
                value={bold}
                onChange={(v) => v !== null && onChangeBold(v)}
                showStepBtn={false}
                precision={0}
                min={MIN_BORDER_WIDTH}
                // 加了性能也不好(没做处理), 所以先去掉
                enableMouseWheel={false}
            />
            <Popover
                showArrow={false}
                destroyTooltipOnHide
                overlayInnerStyle={{ transform: 'translate(6px, -4px)' }}
                overlayClassName="table-panel__border-style-popover"
                content={
                    <ul className="table-panel__border-style-list">
                        {[
                            { value: 'line-0', svg: line0Svg },
                            { value: 'line-1', svg: line1Svg },
                            { value: 'line-2', svg: line2Svg },
                            { value: 'line-3', svg: line3Svg },
                            { value: 'line-4', svg: line4Svg },
                        ].map(({ value, svg }) => (
                            <li
                                key={value}
                                data-active={tempStyleValue === value}
                                style={{ backgroundImage: `url("${svg}")` }}
                                onClick={() => onChangeTempStyleValue(value as BorderStyle)}
                            ></li>
                        ))}
                    </ul>
                }
                trigger="click"
                placement="bottomRight"
            >
                <div style={{ width: 14, height: 14, background: `url("${lineStyleHandlerSvg}")` }}></div>
            </Popover>
        </div>
    );
};

BorderStyleWidget.displayName = 'BorderStyleWidget';

const MergeBtn: React.FC<{
    activeTableInfo: ActiveTableInfo;
    mergeCells: () => void;
    cancelMergeCells: () => void;
}> = ({ activeTableInfo: { hasSelectedInfo, mergeInfo }, mergeCells, cancelMergeCells }) => {
    const { $t } = useLocaleStore();
    const { tableIsCanMerged, tableIsCanCancelMerged, selectedCellsIsCanMerged, selectedCellsIsCanCancelMerged } =
        mergeInfo ?? {};
    const isCanMerged = hasSelectedInfo ? selectedCellsIsCanMerged : tableIsCanMerged;
    const isCanCancelMerged = hasSelectedInfo ? selectedCellsIsCanCancelMerged : tableIsCanCancelMerged;
    return (
        <Button
            disabled={!isCanMerged && !isCanCancelMerged}
            style={{ margin: '19px auto 0 auto', width: '100%' }}
            onClick={isCanMerged ? mergeCells : cancelMergeCells}
        >
            {isCanMerged ? $t((v) => v.tableLayer.mergeCell) : $t((v) => v.tableLayer.cancelMergeCell)}
        </Button>
    );
};

MergeBtn.displayName = 'MergeBtn';
